/**
 *  Formats arrays of strings into an English string by quoting each string
 *   (and escaping special characters through JSON formatting)
 *   and inserting commas, spaces, and either "and" or "or" as appropriate
 *
 *  examples: (...inputs) -> output
 *
 *    (['a', 'b', 'c']) -> `"a", "b", and "c"`
 *
 *    (['a', 'b', 'c'], 'or') -> `"a", "b", or "c"`
 *
 *    (['a', 'b']) => `"a" and "b"`
 *
 *    (['a']) -> `"a"`
 *
 *    (['""']) -> `"\\"\\""`
 *
 *    ([]) -> ``
 *
 *  A potential use case is dynamically (English) human-readable error messages
 *   when the message is associated with a specific array of strings
 */
export const anglicizeStringArray = (
  stringArray: string[],
  andOrOr: 'and' | 'or' = 'and',
) => {
  const quotedStrings = stringArray.map((s) => JSON.stringify(s))
  switch (quotedStrings.length) {
    case 0:
      return ''
    case 1:
      return quotedStrings[0]
    case 2:
      return quotedStrings.join(` ${andOrOr} `)
    default:
      return `${quotedStrings.slice(0, -1).join(', ')}, ${andOrOr} ${
        quotedStrings[quotedStrings.length - 1]
      }`
  }
}

const isInvalidIndex = <T>(array: T[], index: number) =>
  !Number.isInteger(index) || index < 0 || index >= array.length

/**
 * Returns a new array with the value at the specified index moved to be
 *  at another index (other elements are otherwise kept in order)
 *
 * @param oldValuesArr Array before movement (not to be mutated)
 * @param moveFromIndex the index of the value to be moved in the old array
 * @param moveToIndex the index of the value (in the old array) that we are inserting the value after
 */
export const moveTo = <T>(
  oldValuesArr: T[],
  moveFromIndex: number,
  moveToIndex: number,
) => {
  if (isInvalidIndex(oldValuesArr, moveFromIndex)) {
    throw new Error(
      `The supplied moveFromIndex "${moveFromIndex}" is not a valid index for an array of length ${oldValuesArr.length}`,
    )
  }
  if (isInvalidIndex(oldValuesArr, moveToIndex)) {
    throw new Error(
      `The supplied moveToIndex "${moveToIndex}" is not a valid index for an array of length ${oldValuesArr.length}`,
    )
  }
  return moveFromIndex === moveToIndex
    ? [...oldValuesArr]
    : oldValuesArr.flatMap((value, index, arr) =>
        index === moveFromIndex
          ? []
          : index === moveToIndex
          ? [moveFromIndex, moveToIndex]
              .sort((a, b) => b - a)
              .map((index) => arr[index])
          : [value],
      )
}
